import { Button, ButtonGroup, DeleteIcon, EditIcon, Table, TableColumns } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useSubstitutionDateRange } from '../../../hooks/useSubstitutionDateRange';
import { useAbsencesQuery } from '../../../types/planung-graphql-client-defs';
import { getAbsenceReasonTranslation } from '../../../utils/absenceReasons';

export type TeacherAbsenceTableType = {
  uuid: string;
  teacherName: string;
  start: string;
  end: string;
  reason: string;
  comment: string;
};

type TeacherAbsencesTableProps = {
  loading: boolean;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
};

export const TeacherAbsencesTable = observer(({ loading, onAdd, onEdit, onDelete }: TeacherAbsencesTableProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { from, to } = useSubstitutionDateRange();
  const context = useMemorizedCacheTag('ABSENCES');
  const currentSchoolYear = useSelectedSchoolYear();

  const [{ data }] = useAbsencesQuery({
    variables: {
      start: from.startOf('day').utc().toISOString(),
      end: to.endOf('day').utc().toISOString(),
    },
    context,
    pause: !currentSchoolYear,
  });

  const absences: TeacherAbsenceTableType[] =
    data?.absencesInRange.map((a) => {
      return {
        uuid: a.uuid,
        teacherName: a.personListName,
        start: dayjs(a.start).format('DD.MM.YYYY HH:mm'),
        end: dayjs(a.start).isSame(a.end, 'day')
          ? dayjs(a.end).format('HH:mm')
          : dayjs(a.end).format('DD.MM.YYYY HH:mm'),
        reason: getAbsenceReasonTranslation(a.reason),
        comment: a.comment ?? '',
      };
    }) ?? [];

  const columns: TableColumns<TeacherAbsenceTableType>[] = [
    {
      header: t('persons.titleNeutral'),
      id: 'teacherName',
      accessorKey: 'teacherName',
      size: 250,
    },
    {
      header: t('common.start'),
      id: 'start',
      size: 140, // „21.09.2026 00:00“ vollständig
      accessorKey: 'start',
    },
    {
      header: t('common.end'),
      id: 'end',
      size: 140, // „21.09.2026 00:00“ vollständig
      accessorKey: 'end',
    },
    {
      header: t('absences.reason'),
      id: 'reason',
      accessorKey: 'reason',
      size: 150,
    },
    {
      header: t('common.comment'),
      id: 'comment',
      accessorKey: 'comment',
      size: 250,
    },
  ];

  return (
    <Table<TeacherAbsenceTableType>
      columns={columns}
      data={absences}
      showShadow
      showBorderRadius
      loading={loading}
      showActionBar
      actionBarSettings={{ showAddButton: true, showExpertFilter: true }}
      onAddClick={onAdd}
      isOnWhite={false}
      // mobil als Karten (wie BP) – Antippen öffnet direkt das Bearbeiten
      isDesktop={!isMobile}
      onRowClick={isMobile ? (_, row) => onEdit(row.original.uuid) : undefined}
      lastColWidth='80px'
      lastCol={(row) => {
        return (
          <ButtonGroup>
            <Button
              hierarchy='secondary'
              onClick={() => onEdit(row.original.uuid)}
              icon={<EditIcon className='small' />}
            />
            <Button
              hierarchy='secondary'
              onClick={() => onDelete(row.original.uuid)}
              icon={<DeleteIcon className='small' color='var(--color-error)' />}
            />
          </ButtonGroup>
        );
      }}
    />
  );
});
